import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Contact, Loader2, Package, Search, ShoppingCart, type LucideIcon } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { ADMIN_SEARCH_MIN_LENGTH, useAdminSearch } from '@/hooks/queries/useAdminSearch';
import { useDebounce } from '@/hooks/useDebounce';
import { useI18n } from '@/hooks/useI18n';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/utils/currency';
import { getLocalizedText } from '@/utils/i18n';

interface ResultItem {
  key: string;
  group: 'customers' | 'orders' | 'packages';
  title: string;
  subtitle: string;
  href: string;
}

const GROUP_ICONS: Record<ResultItem['group'], LucideIcon> = {
  customers: Contact,
  orders: ShoppingCart,
  packages: Package,
};

/**
 * Header search across customers, orders and packages. Press Ctrl/⌘+K to
 * focus it, arrow keys to move, Enter to open a result, Escape to close.
 * Only groups the admin may read come back from the backend.
 */
export function AdminGlobalSearch() {
  const { lang, t } = useI18n();
  const navigate = useNavigate();
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const debounced = useDebounce(term, 250);
  const searchQuery = useAdminSearch(debounced);
  const ready = debounced.trim().length >= ADMIN_SEARCH_MIN_LENGTH;

  const items = useMemo<ResultItem[]>(() => {
    const data = ready ? searchQuery.data : undefined;
    if (!data) return [];
    return [
      ...(data.customers ?? []).map((c) => ({
        key: `c-${c.id}`,
        group: 'customers' as const,
        title: c.nickname || c.email,
        subtitle: `${c.email} · ID ${c.id}`,
        href: `/admin/customers/${c.id}`,
      })),
      ...(data.orders ?? []).map((o) => ({
        key: `o-${o.id}`,
        group: 'orders' as const,
        title: o.order_no,
        subtitle: [
          getLocalizedText(o.package_title, lang, ''),
          o.customer_nickname,
          formatCurrency(o.total_amount, o.currency, lang),
          t(`order.status.${o.status}`),
        ]
          .filter(Boolean)
          .join(' · '),
        href: `/admin/orders?order=${o.id}`,
      })),
      ...(data.packages ?? []).map((p) => ({
        key: `p-${p.id}`,
        group: 'packages' as const,
        title: getLocalizedText(p.title, lang, p.slug),
        subtitle: `${p.slug} · ${t(`admin.search.packageStatus.${p.status}`)}`,
        href: `/admin/packages/${p.id}/edit`,
      })),
    ];
  }, [ready, searchQuery.data, lang, t]);

  // Ctrl/⌘+K focuses the search from anywhere in the admin.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // Close when clicking outside.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  const choose = (item: ResultItem) => {
    setOpen(false);
    setTerm('');
    inputRef.current?.blur();
    navigate(item.href);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
      return;
    }
    if (!items.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((index) => (index + 1) % items.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => (index - 1 + items.length) % items.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const item = items[Math.min(activeIndex, items.length - 1)];
      if (item) choose(item);
    }
  };

  const showPanel = open && term.trim().length >= ADMIN_SEARCH_MIN_LENGTH;
  const activeItem = showPanel ? items[activeIndex] : undefined;
  let lastGroup: ResultItem['group'] | null = null;

  return (
    <div ref={containerRef} className="relative w-full max-w-md">
      <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" aria-hidden="true" />
      <Input
        ref={inputRef}
        role="combobox"
        aria-expanded={showPanel}
        aria-controls={listId}
        aria-activedescendant={activeItem ? `${listId}-${activeItem.key}` : undefined}
        aria-label={t('admin.search.label')}
        value={term}
        placeholder={t('admin.search.placeholder')}
        className="h-9 bg-muted/40 pl-8 pr-14"
        onChange={(event) => {
          setTerm(event.target.value);
          setActiveIndex(0);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />
      <kbd className="pointer-events-none absolute right-2 top-2 hidden rounded border bg-white px-1.5 text-[10px] text-muted-foreground sm:block">
        Ctrl K
      </kbd>

      {showPanel && (
        <div className="absolute left-0 right-0 top-11 z-50 max-h-[70vh] overflow-y-auto rounded-xl border bg-white p-1 shadow-lg">
          {searchQuery.isFetching && items.length === 0 ? (
            <div className="flex items-center gap-2 px-3 py-4 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              {t('admin.search.searching')}
            </div>
          ) : items.length === 0 ? (
            <p className="px-3 py-4 text-sm text-muted-foreground">{t('admin.search.noResults')}</p>
          ) : (
            <ul id={listId} role="listbox" aria-label={t('admin.search.label')}>
              {items.map((item, index) => {
                const Icon = GROUP_ICONS[item.group];
                const header = item.group !== lastGroup ? item.group : null;
                lastGroup = item.group;
                return (
                  <li key={item.key} role="presentation">
                    {header && (
                      <p className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        {t(`admin.search.group.${header}`)}
                      </p>
                    )}
                    <button
                      id={`${listId}-${item.key}`}
                      type="button"
                      role="option"
                      aria-selected={index === activeIndex}
                      onMouseEnter={() => setActiveIndex(index)}
                      onClick={() => choose(item)}
                      className={cn(
                        'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left',
                        index === activeIndex ? 'bg-primary/10' : 'hover:bg-muted',
                      )}
                    >
                      <Icon className="h-4 w-4 flex-shrink-0 text-muted-foreground" aria-hidden="true" />
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium">{item.title}</span>
                        <span className="block truncate text-xs text-muted-foreground">{item.subtitle}</span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
